import { isProduction } from '@libs/core';
import { FirebaseApp, getApps, initializeApp } from 'firebase/app';
import { fetchAndActivate, getAll, getRemoteConfig, RemoteConfig } from 'firebase/remote-config';
import { useEffect, useMemo, useSyncExternalStore } from 'react';

const DEFAULT_FIREBASE_CONFIG = {
    apiKey: 'AIzaSyBtT4CJXRHkJdrOM2NdPGUKoOXifeYkANI',
    authDomain: 'paysend-67ec2.firebaseapp.com',
    databaseURL: 'https://paysend-67ec2.firebaseio.com',
    projectId: 'paysend-67ec2',
    storageBucket: 'paysend-67ec2.appspot.com',
    messagingSenderId: '65660059720',
    appId: '1:65660059720:web:183793ea0b33b7e999587d',
    measurementId: 'G-JDYSKWCQPX',
};

const DEFAULT_FETCH_TIMEOUT_MS = 10000;
const DEFAULT_MAX_RETRIES = 3;
const DEFAULT_RETRY_DELAY_MS = 1500;
export const DEFAULT_PROD_MIN_FETCH_INTERVAL_MS = 3600000; // 1 hour for production quota preservation
export const DEFAULT_DEV_MIN_FETCH_INTERVAL_MS = 60000; // 1 min for instant updates during development

/**
 * Resolves the minimum fetch interval for Remote Config.
 * - Prioritizes NX_PUBLIC_FIREBASE_MIN_FETCH_INTERVAL_MS if configured.
 * - In development (process.env.NODE_ENV !== 'production'): 0 ms so changes are visible immediately.
 * - In production: 3 600 000 ms (1 hour) to safely protect Remote Config quotas.
 */
export const getMinimumFetchIntervalMillis = (): number => {
    const customInterval = process.env.NX_PUBLIC_FIREBASE_MIN_FETCH_INTERVAL_MS;
    if (customInterval) {
        const parsed = Number(customInterval);
        if (!Number.isNaN(parsed) && parsed >= 0) {
            return parsed;
        }
    }
    return isProduction() ? DEFAULT_PROD_MIN_FETCH_INTERVAL_MS : DEFAULT_DEV_MIN_FETCH_INTERVAL_MS;
};

export type FlagsDictionary = Record<string, string>;

export type RemoteConfigStatus = 'idle' | 'loading' | 'success' | 'error';

export interface RemoteConfigSnapshot {
    readonly status: RemoteConfigStatus;
    readonly flags: Record<string, string> | null;
    readonly error: Error | null;
}

export type FirebaseFlagsResult<T extends Record<string, unknown> = FlagsDictionary> = T &
    Record<string, unknown> & {
        readonly isLoaded: boolean;
        readonly isLoading: boolean;
        readonly isError: boolean;
        readonly error: Error | null;
        readonly refetch: () => Promise<Record<string, string>>;
    };

export interface FetchRemoteFlagsOptions {
    /** Force a new network fetch even if flags are already cached or in error state */
    force?: boolean;
    /** Current retry attempt number (0-indexed, internal use) */
    attempt?: number;
    /** Maximum number of retry attempts on network error (default: 3) */
    maxRetries?: number;
    /** Base delay in milliseconds for exponential backoff (default: 1500) */
    retryDelayMs?: number;
}

let firebaseAppInstance: FirebaseApp | null = null;
let remoteConfigInstance: RemoteConfig | null = null;
let currentState: RemoteConfigSnapshot = {
    status: 'idle',
    flags: null,
    error: null,
};
let fetchPromise: Promise<Record<string, string>> | null = null;
let retryTimer: ReturnType<typeof setTimeout> | null = null;
let isOnlineListenerAttached = false;
let lastUsedDefaults: Record<string, unknown> | undefined = undefined;
let lastUsedConfig: Record<string, string> = DEFAULT_FIREBASE_CONFIG;
const listeners = new Set<() => void>();

const getFirebaseApp = (config: Record<string, string> = DEFAULT_FIREBASE_CONFIG): FirebaseApp => {
    if (firebaseAppInstance) {
        return firebaseAppInstance;
    }
    const apps = getApps();
    if (apps.length > 0) {
        firebaseAppInstance = apps[0];
        return firebaseAppInstance;
    }
    firebaseAppInstance = initializeApp(config);
    return firebaseAppInstance;
};

const getFirebaseRemoteConfig = (app?: FirebaseApp): RemoteConfig | null => {
    if (typeof window === 'undefined') {
        return null;
    }
    if (remoteConfigInstance) {
        return remoteConfigInstance;
    }
    const targetApp = app || getFirebaseApp();
    remoteConfigInstance = getRemoteConfig(targetApp);
    return remoteConfigInstance;
};

const notifyListeners = (): void => {
    listeners.forEach((listener) => {
        try {
            listener();
        } catch (e) {
            console.error('Error in flags listener:', e);
        }
    });
};

const handleOnline = (): void => {
    if (currentState.status === 'error' || currentState.status === 'idle') {
        if (retryTimer !== null) {
            clearTimeout(retryTimer);
            retryTimer = null;
        }
        void fetchRemoteFlags(lastUsedDefaults, lastUsedConfig, { force: true, attempt: 0 });
    }
};

const setupOnlineListener = (): void => {
    if (typeof window !== 'undefined' && typeof window.addEventListener === 'function' && !isOnlineListenerAttached) {
        window.addEventListener('online', handleOnline);
        isOnlineListenerAttached = true;
    }
};

const removeOnlineListener = (): void => {
    if (typeof window !== 'undefined' && typeof window.removeEventListener === 'function' && isOnlineListenerAttached) {
        window.removeEventListener('online', handleOnline);
        isOnlineListenerAttached = false;
    }
};

export function createFlagsResult<T extends Record<string, unknown>>(
    defaults?: T,
    remote?: Record<string, unknown> | null,
    status?: RemoteConfigStatus,
    error: Error | null = null,
): FirebaseFlagsResult<T> {
    const base: Record<string, unknown> = { ...(defaults || {}) };
    if (remote) {
        Object.assign(base, remote);
    }

    const isLoaded = status ? status === 'success' : remote !== null && remote !== undefined;
    const isLoading = status ? status === 'loading' : !isLoaded;
    const isError = status ? status === 'error' : false;

    Object.defineProperty(base, 'isLoaded', {
        get() {
            return isLoaded;
        },
        enumerable: false,
        configurable: true,
    });

    Object.defineProperty(base, 'isLoading', {
        get() {
            return isLoading;
        },
        enumerable: false,
        configurable: true,
    });

    Object.defineProperty(base, 'isError', {
        get() {
            return isError;
        },
        enumerable: false,
        configurable: true,
    });

    Object.defineProperty(base, 'error', {
        get() {
            return error;
        },
        enumerable: false,
        configurable: true,
    });

    Object.defineProperty(base, 'refetch', {
        value: () => refetchRemoteFlags(defaults),
        enumerable: false,
        configurable: true,
        writable: true,
    });

    return base as FirebaseFlagsResult<T>;
}

const subscribe = (callback: () => void): (() => void) => {
    listeners.add(callback);
    return () => {
        listeners.delete(callback);
    };
};

const getSnapshot = (): RemoteConfigSnapshot => currentState;

const SERVER_SNAPSHOT: RemoteConfigSnapshot = {
    status: 'idle',
    flags: null,
    error: null,
};
const getServerSnapshot = (): RemoteConfigSnapshot => SERVER_SNAPSHOT;

export const getRemoteConfigSnapshot = (): RemoteConfigSnapshot => currentState;

/**
 * Fetches Remote Config from Firebase with strict single-fetch deduplication and retry logic.
 * - If already cached and not forced, resolves immediately with cached flags.
 * - If a request is in flight, reuses the pending promise.
 * - Only 1 network request is made across all calls and components at a time.
 * - On failure:
 *   - Immediately updates external store state (isLoading -> false, isError -> true) and notifies listeners.
 *   - Automatically retries with exponential backoff up to `maxRetries` (default: 3).
 *   - Resolves with cached flags or empty object without throwing, preserving fallback values.
 * - Automatically retries when browser comes back online (`window.ononline`).
 */
export const fetchRemoteFlags = async (
    defaults?: Record<string, unknown>,
    config: Record<string, string> = DEFAULT_FIREBASE_CONFIG,
    options: FetchRemoteFlagsOptions = {},
): Promise<Record<string, string>> => {
    const { force = false, attempt = 0, maxRetries = DEFAULT_MAX_RETRIES, retryDelayMs = DEFAULT_RETRY_DELAY_MS } = options;

    if (!force && currentState.status === 'success' && currentState.flags !== null) {
        return currentState.flags;
    }

    if (fetchPromise !== null) {
        return fetchPromise;
    }

    if (retryTimer !== null && attempt === 0) {
        clearTimeout(retryTimer);
        retryTimer = null;
    }

    lastUsedDefaults = defaults ?? lastUsedDefaults;
    lastUsedConfig = config;
    setupOnlineListener();

    currentState = {
        status: 'loading',
        flags: currentState.flags,
        error: null,
    };
    notifyListeners();

    fetchPromise = (async () => {
        try {
            if (typeof window === 'undefined') {
                currentState = {
                    status: 'idle',
                    flags: null,
                    error: null,
                };
                notifyListeners();
                return {};
            }

            const app = getFirebaseApp(config);
            const rConfig = getFirebaseRemoteConfig(app);
            if (!rConfig) {
                currentState = {
                    status: 'idle',
                    flags: null,
                    error: null,
                };
                notifyListeners();
                return {};
            }

            if (defaults) {
                rConfig.defaultConfig = defaults as Record<string, string | number | boolean>;
            }

            rConfig.settings = {
                fetchTimeoutMillis: DEFAULT_FETCH_TIMEOUT_MS,
                minimumFetchIntervalMillis: getMinimumFetchIntervalMillis(),
            };

            await fetchAndActivate(rConfig);

            const allConfig = getAll(rConfig);
            const flags: Record<string, string> = {};

            for (const [key, value] of Object.entries(allConfig)) {
                flags[key] = value.asString();
            }

            currentState = {
                status: 'success',
                flags,
                error: null,
            };

            notifyListeners();

            return flags;
        } catch (error) {
            const err = error instanceof Error ? error : new Error(String(error));
            console.error('Failed to fetch Firebase remote config:', err);

            currentState = {
                status: 'error',
                flags: currentState.flags,
                error: err,
            };

            notifyListeners();

            if (attempt < maxRetries && typeof window !== 'undefined') {
                const delay = retryDelayMs * Math.pow(2, attempt);
                retryTimer = setTimeout(() => {
                    retryTimer = null;
                    void fetchRemoteFlags(defaults, config, {
                        force: true,
                        attempt: attempt + 1,
                        maxRetries,
                        retryDelayMs,
                    });
                }, delay);
            }

            return currentState.flags ?? {};
        } finally {
            fetchPromise = null;
        }
    })();

    return fetchPromise;
};

/**
 * Triggers a fresh remote config fetch, bypassing existing cache and resetting retry count.
 */
export const refetchRemoteFlags = (
    defaults?: Record<string, unknown>,
    config: Record<string, string> = DEFAULT_FIREBASE_CONFIG,
): Promise<Record<string, string>> => {
    if (retryTimer !== null) {
        clearTimeout(retryTimer);
        retryTimer = null;
    }
    return fetchRemoteFlags(defaults, config, { force: true, attempt: 0 });
};

export const resetFirebaseConfigForTesting = (): void => {
    if (retryTimer !== null) {
        clearTimeout(retryTimer);
        retryTimer = null;
    }
    removeOnlineListener();
    currentState = {
        status: 'idle',
        flags: null,
        error: null,
    };
    fetchPromise = null;
    listeners.clear();
    firebaseAppInstance = null;
    remoteConfigInstance = null;
    lastUsedDefaults = undefined;
    lastUsedConfig = DEFAULT_FIREBASE_CONFIG;
};

/**
 * Hook to access Firebase Remote Config.
 * Guarantees that config is fetched only once, avoiding duplicate requests
 * even when called concurrently or repeatedly across multiple components.
 *
 * State semantics:
 * - `isLoaded`: `true` once remote flags have been successfully fetched and activated.
 * - `isLoading`: `true` ONLY while a network request is in flight. False when idle, success, or error.
 * - `isError`: `true` if the remote config fetch failed.
 * - `error`: The error object if the fetch failed, or `null`.
 * - `flags`: Object containing remote flags merged on top of `defaults`.
 * - `refetch`: Function to manually trigger a fresh fetch.
 *
 * Resilience & Retries:
 * - On transient failures (e.g. cold start on unstable network), `isLoading` drops to `false`
 *   and subscribers are immediately notified so they can fall back to `defaults`.
 * - Automatic exponential backoff retries (up to 3 attempts: 1.5s, 3s, 6s).
 * - Automatic retry as soon as network connectivity is restored (`online` event).
 */
export const useFirebaseConfig = <T extends Record<string, unknown> = FlagsDictionary>(defaults?: T): FirebaseFlagsResult<T> => {
    const snapshot = useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);

    useEffect(() => {
        setupOnlineListener();
        if (currentState.status === 'idle') {
            void fetchRemoteFlags(defaults);
        }
    }, [defaults]);

    return useMemo(() => {
        return createFlagsResult(defaults, snapshot.flags, snapshot.status, snapshot.error);
    }, [defaults, snapshot]);
};
